import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { Section, Eyebrow, Countdown } from "@/components/domain/Bits";
import { Button } from "@/components/ui/button";
import { Radio, Play, Trophy, ArrowRight, Sparkles } from "lucide-react";

const PHASES = ["Generando resultado", "Número ganador", "Buscando participante", "Revisión", "Ganador confirmado"];

export default function LiveDraw() {
  const [raffles, setRaffles] = useState([]);
  const [phase, setPhase] = useState(-1);
  const [winning, setWinning] = useState(null);
  const [rolling, setRolling] = useState("0000");
  useEffect(() => { api.get("/raffles").then((r) => setRaffles(r.data)); }, []);
  const next = raffles.find((r) => r.status === "active") || raffles[0];
  const winner = raffles.find((r) => r.status === "sold_out") || next;

  const run = () => {
    setPhase(0); setWinning(null);
    let n = 0;
    const roll = setInterval(() => { setRolling(String(Math.floor(Math.random() * 9999)).padStart(4, "0")); }, 80);
    setTimeout(() => setPhase(1), 1800);
    setTimeout(() => { clearInterval(roll); const w = String(Math.floor(Math.random() * (winner?.total_tickets || 5000)) + (winner?.number_start ?? 0)).padStart(4, "0"); setWinning(w); setRolling(w); }, 2600);
    setTimeout(() => setPhase(2), 3200);
    setTimeout(() => setPhase(3), 4200);
    setTimeout(() => setPhase(4), 5200);
  };

  return (
    <div className="min-h-screen serif-scope trust-gradient text-white">
      <Section className="py-14">
        <div className="flex items-center justify-between">
          <Eyebrow><Radio className="h-3.5 w-3.5" />Sala del Sorteo</Eyebrow>
          {phase >= 0 && <span className="inline-flex items-center gap-2 rounded-full bg-red-500 px-3 py-1 text-xs font-bold"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />EN VIVO</span>}
        </div>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <div className="aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black/40">
              <div className="grid h-full place-items-center">
                {phase < 0 ? (
                  <div className="text-center">
                    <img src={winner?.gallery?.[0]} alt="" className="mx-auto mb-6 h-40 rounded-xl object-cover opacity-60" />
                    <h2 className="text-2xl font-bold">{winner?.name}</h2>
                    <p className="mt-2 text-slate-400">La transmisión comenzará pronto</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <AnimatePresence mode="wait">
                      <motion.div key={phase} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mb-6 text-sm font-semibold uppercase tracking-widest text-blue-300">
                        {PHASES[phase]}
                      </motion.div>
                    </AnimatePresence>
                    <motion.div animate={phase === 4 ? { scale: [1, 1.15, 1] } : {}} transition={{ duration: 0.6 }} className={`font-mono text-7xl font-bold tracking-tight sm:text-8xl ${phase === 4 ? "text-emerald-400" : "text-white"}`}>
                      #{winning || rolling}
                    </motion.div>
                    {phase === 4 && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6">
                        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/20 px-4 py-2 text-emerald-300"><Sparkles className="h-4 w-4" />Ganador confirmado</div>
                      </motion.div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex gap-3">
              {phase < 0 ? (
                <Button onClick={run} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="start-draw"><Play className="mr-2 h-4 w-4" />Iniciar demostración del sorteo</Button>
              ) : phase === 4 ? (
                <Button asChild className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Link to="/ganadores">Ver perfil del ganador <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              ) : (
                <Button disabled className="rounded-full">Sorteo en proceso...</Button>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="font-semibold">Antes del evento</h3>
              <p className="mt-1 text-sm text-slate-400">Cuenta regresiva para el próximo sorteo</p>
              {next && <div className="mt-4"><Countdown target={next.draw_date} className="text-white" /></div>}
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="font-semibold">Información del sorteo</h3>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-slate-400">Premio</dt><dd className="font-medium">{winner?.prize}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-400">Método</dt><dd className="font-medium">{winner?.method}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-400">Boletos</dt><dd className="font-mono font-medium">{winner?.total_tickets}</dd></div>
              </dl>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-sm text-slate-300">
              <Trophy className="mb-2 h-5 w-5 text-amber-400" />
              El resultado permanece <b className="text-white">bajo revisión</b> antes de su publicación oficial, garantizando transparencia total.
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
